/* ──────────────────────────────────────────────
   QUESTIONS DATA — 9 Questions across 3 Sections
   ────────────────────────────────────────────── */

const QUESTIONS = [
  // ── SECTION 1: CUSTOMER INTELLIGENCE ──
  {
    index: 0,
    section: 1,
    sectionName: "Customer Intelligence",
    weight: 30,
    tag: "Customer Data",
    cardTitle: "360° Customer View",
    text: "Do you have a 360° view of each customer?",
    note: "Data unification is the foundational constraint on everything downstream.",
    whyItMatters: "A unified customer identity is the foundation for every personalised interaction downstream.",
    options: [
      {
        letter: "A", score: 0,
        text: "Online and offline are completely separate",
        example: "A customer's in-store and online purchases cannot be linked to the same identity."
      },
      {
        letter: "B", score: 5,
        text: "Purchase history is unified, but pre-purchase behaviour isn't",
        example: "You know what customers bought across channels, but browsing sessions don't flow into the customer profile."
      },
      {
        letter: "C", score: 8,
        text: "Unified profile across transactions and digital behaviour",
        example: "Purchases, pre-purchase browsing & app sessions all form one continuous customer timeline."
      },
      {
        letter: "D", score: 10,
        text: "A complete 360° view",
        example: "Every transaction, digital interaction, support history, and NPS score forms a single customer record."
      }
    ]
  },
  {
    index: 1,
    section: 1,
    sectionName: "Customer Intelligence",
    weight: 30,
    tag: "Analytics",
    cardTitle: "Measure & Diagnose",
    text: "How well can you measure and diagnose your customer behaviour?",
    note: "Slicing deeply to identify root causes is what separates a reactive CRM from an intelligent one.",
    whyItMatters: "Diagnostic depth separates brands that react to churn from those that prevent it.",
    options: [
      {
        letter: "A", score: 0,
        text: "High-level reporting only",
        example: "You have headline numbers but cannot investigate how they differ across cohorts or stores."
      },
      {
        letter: "B", score: 3,
        text: "Single dimensional analysis",
        example: "You can analyse metrics separately by cohorts, tiers or stores."
      },
      {
        letter: "C", score: 7,
        text: "Multi-dimensional diagnostics",
        example: "You can slice across tiers, stores, categories, and campaigns simultaneously to identify exactly where."
      },
      {
        letter: "D", score: 10,
        text: "Proactive AI intelligence",
        example: "The system surfaces cross-dimensional opportunities and anomalies without being asked — intelligence is pushed to marketers."
      }
    ]
  },
  {
    index: 2,
    section: 1,
    sectionName: "Customer Intelligence",
    weight: 40,
    tag: "Predictive AI",
    cardTitle: "Predict Behaviour",
    text: "Can your system predict which customers are about to disengage, which are ready to buy again, and what they'll likely buy next — before it happens?",
    note: "This is the single biggest lever separating a capable CRM from a truly high-performing one.",
    whyItMatters: "Predicting behaviour before it happens converts your CRM from a broadcast tool into a revenue engine.",
    options: [
      {
        letter: "A", score: 0,
        text: "No predictive capability",
        example: "All targeting is based on what customers have already done — you find out a customer has lapsed only after they've left."
      },
      {
        letter: "B", score: 3,
        text: "A prediction exists but isn't used in campaigns",
        example: "A churn risk score may appear in a dashboard, but it hasn't crossed the gap into informing who actually gets contacted."
      },
      {
        letter: "C", score: 7,
        text: "Multiple active models used to build campaign audiences",
        example: "Churn propensity, next purchase likelihood, and product affinity scores are live and directly used to build and refresh campaign audiences."
      },
      {
        letter: "D", score: 10,
        text: "Predictive & recommendation model creator",
        example: "When a Gold customer's churn propensity crosses a threshold, a personalised win-back journey starts automatically within the hour — no human trigger required."
      }
    ]
  },

  // ── SECTION 2: COMMUNICATION EXECUTION ──
  {
    index: 3,
    section: 2,
    sectionName: "Communication Execution",
    weight: 40,
    tag: "Journey Automation",
    cardTitle: "Triggered Journeys",
    text: "How automated and sophisticated are your triggered customer journeys?",
    note: null,
    whyItMatters: "Journey automation ensures every key customer moment is captured and acted on at scale.",
    options: [
      {
        letter: "A", score: 0,
        text: "Manual process",
        example: "Lists are downloaded and sent manually — nothing runs automatically in response to customer behaviour."
      },
      {
        letter: "B", score: 3,
        text: "Adhoc campaigns",
        example: "Campaigns sent via a CRM or marketing platform. No CLM lifecycle."
      },
      {
        letter: "C", score: 7,
        text: "Key lifecycle journeys running automatically",
        example: "Multi step welcome, win-back, birthday, and tier upgrade journeys fire automatically."
      },
      {
        letter: "D", score: 10,
        text: "AI creates individual customer strategy",
        example: "AI Decisioning Agents determine the best channel, content, and time for each customer."
      }
    ]
  },
  {
    index: 4,
    section: 2,
    sectionName: "Communication Execution",
    weight: 20,
    tag: "Channel Intelligence",
    cardTitle: "Channel Routing",
    text: "When you send a customer a message, how intelligently does your system decide which channel to use?",
    note: null,
    whyItMatters: "Reaching customers on their preferred channel maximises engagement and eliminates wasted spend.",
    options: [
      {
        letter: "A", score: 0,
        text: "No channel intelligence",
        example: "Ad-hoc decisioning to send communications across channels."
      },
      {
        letter: "B", score: 2,
        text: "Compliance only",
        example: "DND lists are auto filtered to optimise communication budget."
      },
      {
        letter: "C", score: 7,
        text: "Rule-based fallback and smart retry",
        example: "Auto retry to increase Whatsapp retry or use fallback channel in campaigns & journeys to increase reach."
      },
      {
        letter: "D", score: 10,
        text: "AI channel affinity",
        example: "The system learns each customer's preferred channel and routes automatically."
      }
    ]
  },
  {
    index: 5,
    section: 2,
    sectionName: "Communication Execution",
    weight: 20,
    tag: "Personalisation",
    cardTitle: "Message Relevance",
    text: "When a customer receives a message from you, how relevant is it to them specifically?",
    note: null,
    whyItMatters: "Messages that reflect individual behaviour drive meaningfully higher conversion than broadcast communications.",
    options: [
      {
        letter: "A", score: 0,
        text: "Same message for everyone",
        example: "The same content, image, and offer to the entire segment."
      },
      {
        letter: "B", score: 3,
        text: "Basic personalisation",
        example: "The message includes the customer's name, tier, or points balance."
      },
      {
        letter: "C", score: 7,
        text: "Personalised text based recommendations",
        example: "Using favorite and next best recommendations to personalize text content."
      },
      {
        letter: "D", score: 10,
        text: "Fully personalised images + incentives",
        example: "Visual product recommendations, and personalised offer/ loyalty incentive for each customer."
      }
    ]
  },
  {
    index: 6,
    section: 2,
    sectionName: "Communication Execution",
    weight: 20,
    tag: "Measurement",
    cardTitle: "Campaign Measurement",
    text: "What level of understanding do you have about communication performance?",
    note: null,
    whyItMatters: "Without incrementality measurement, CRM investment cannot be proven or optimised with confidence.",
    options: [
      {
        letter: "A", score: 0,
        text: "Sends, opens, and clicks only",
        example: "Revenue cannot be reliably connected to specific campaigns."
      },
      {
        letter: "B", score: 3,
        text: "Attributed revenue",
        example: "You can report revenue from customers who received the communication."
      },
      {
        letter: "C", score: 7,
        text: "Incremental revenue using control group",
        example: "Campaigns include a matched holdout group to report true incremental revenue."
      },
      {
        letter: "D", score: 10,
        text: "Goal & communication type insights",
        example: "Go beyond conversions to business goals & identify the type of content, audiences that drove impact."
      }
    ]
  },

  // ── SECTION 3: OMNI-CHANNEL LOYALTY & OFFERS ──
  {
    index: 7,
    section: 3,
    sectionName: "Omni-Channel Loyalty & Offers",
    weight: 50,
    tag: "Loyalty Programme",
    cardTitle: "Loyalty Programme",
    text: "How would you describe your loyalty program today?",
    note: "The most sophisticated programs lead with recognition and experience — not just discount.",
    whyItMatters: "A programme with experiential benefits creates emotional lock-in that no competitor can easily replicate.",
    options: [
      {
        letter: "A", score: 2,
        text: "No loyalty program",
        example: "No specific benefits for loyal customers."
      },
      {
        letter: "B", score: 4,
        text: "Basic loyalty program",
        example: "Members earn points or unlock discounts."
      },
      {
        letter: "C", score: 5,
        text: "Status-based tiers",
        example: "3+ tiers with meaningfully different earn rates and benefits."
      },
      {
        letter: "D", score: 10,
        text: "Relationship loyalty with experiential benefits",
        example: "Top tiers offer exclusive experiences and service privileges — creating emotional lock-in no competitor can easily replicate."
      }
    ]
  },
  {
    index: 8,
    section: 3,
    sectionName: "Omni-Channel Loyalty & Offers",
    weight: 50,
    tag: "Offer Management",
    cardTitle: "Omni-Channel Offers",
    text: "How are personalised offers managed across your online and in-store channels?",
    note: "Omnichannel offer management is a key solve for omnichannel customer experience",
    whyItMatters: "Seamless offer management across channels converts CRM campaigns into genuine cross-channel experiences.",
    options: [
      {
        letter: "A", score: 1,
        text: "Completely disconnected",
        example: "Separate offers for online & offline."
      },
      {
        letter: "B", score: 4,
        text: "Channel-specific personalised coupon codes",
        example: "Coupons that work either online or offline."
      },
      {
        letter: "C", score: 8,
        text: "Omnichannel offers with channel wise creation",
        example: "Offers created in each system but customer experience is omnichannel."
      },
      {
        letter: "D", score: 10,
        text: "Seamless omnichannel offers",
        example: "Create offers in CRM and deploy everywhere."
      }
    ]
  },
];

export default QUESTIONS;
